import { DateTime } from 'luxon';

export const TRIPS_CORE_EXPERIENCE_FLAG = 'track-history-trips-core-experience-temporary';
export const HISTORY_CUSTOM_DATE_RANGE_FLAG = 'track-thing-history-custom-date-range-feature';
export const DEFAULT_TRIP_HISTORY_MAX_RANGE_HOURS = 24;

interface HistoryDateRange {
  dateEnd: Date;
  dateStart: Date;
}

export const toPositiveHours = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return;
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    return parsed;
  }

  return;
};

export const resolveTripHistoryMaxRangeHours = (value: unknown): number => {
  return toPositiveHours(value) ?? DEFAULT_TRIP_HISTORY_MAX_RANGE_HOURS;
};

export const buildTripsCoreHistoryRange = (date: DateTime, maxRangeHours: number): HistoryDateRange => {
  return {
    dateEnd: date.toJSDate(),
    dateStart: date.minus({ hours: maxRangeHours }).toJSDate(),
  };
};

export const clampRangeToMaxHours = (
  start: DateTime,
  end: DateTime,
  maxRangeHours: number,
): { end: DateTime; start: DateTime } => {
  const orderedStart = start.toMillis() <= end.toMillis() ? start : end;
  const orderedEnd = start.toMillis() <= end.toMillis() ? end : start;
  const rangeHours = orderedEnd.diff(orderedStart, 'hours').hours;

  if (rangeHours <= maxRangeHours) {
    return {
      end: orderedEnd,
      start: orderedStart,
    };
  }

  return {
    end: orderedEnd,
    start: orderedEnd.minus({ hours: maxRangeHours }),
  };
};

export const isRangeOverMaxHours = (start: Undefinable<DateTime>, end: Undefinable<DateTime>, maxRangeHours: number): boolean => {
  if (!start || !end) return false;
  const normalized = start.toMillis() <= end.toMillis()
    ? { end, start }
    : { end: start, start: end };
  return normalized.end.diff(normalized.start, 'hours').hours > maxRangeHours;
};

export const toMaxRangeDaysLabel = (maxRangeHours: number): string => {
  return `${Math.ceil(maxRangeHours / 24)} days`;
};

type Undefinable<T> = T | undefined;
